/**
 * Autosave of the working state to IndexedDB, so a reload or closed tab never loses work.
 * Writes are debounced; the (possibly large) image is written only when it changes.
 */
import { t } from '../i18n/i18n';
import { type ImageData8, buildProjectFile, saveSession } from '../project/db';
import type { AppState, ImageState, Store } from './store';

const pick = (s: AppState) => [s.history.present, s.project, s.image, s.viewport] as const;

let tokenCounter = 0;
const imageTokens = new WeakMap<ImageState, string>();
function tokenFor(img: ImageState): string {
  let t = imageTokens.get(img);
  if (!t) {
    t = `img_${Date.now().toString(36)}_${++tokenCounter}`;
    imageTokens.set(img, t);
  }
  return t;
}

function imageData(img: ImageState): ImageData8 {
  const b = img.decoded.bytes;
  return {
    bytes: b.buffer.slice(b.byteOffset, b.byteOffset + b.byteLength) as ArrayBuffer,
    mime: img.decoded.mime,
    name: img.decoded.name,
    width: img.decoded.width,
    height: img.decoded.height,
  };
}

export class Autosave {
  private timer: ReturnType<typeof setTimeout> | null = null;
  private last: readonly unknown[] | null = null;
  private savedImageToken: string | null | undefined = undefined;
  private unsubscribe: (() => void) | null = null;
  private writing: Promise<void> = Promise.resolve();
  /** Last error (storage full, private mode …); the UI may show it. */
  error: string | null = null;
  onError: (message: string) => void = () => {};

  constructor(private store: Store, private delay = 600) {}

  /** True while a change has not been written yet. */
  get pending(): boolean {
    return this.timer !== null;
  }

  start(): void {
    if (this.unsubscribe) return;
    this.last = pick(this.store.getState());
    this.unsubscribe = this.store.subscribe(() => {
      const next = pick(this.store.getState());
      if (this.last && next.every((v, i) => v === this.last![i])) return;
      this.last = next;
      this.schedule();
    });
  }

  stop(): void {
    this.unsubscribe?.();
    this.unsubscribe = null;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
  }

  private schedule(): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.flush();
    }, this.delay);
  }

  /** Writes the current state now (also used before leaving the page). */
  flush(): Promise<void> {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    const s = this.store.getState();
    const token = s.image ? tokenFor(s.image) : null;
    const writeImage = token !== null && token !== this.savedImageToken;
    const file = buildProjectFile(s.project.id ?? 'session', s.project.name || t('Untitled'), s.history.present, s.project.createdAt ?? undefined, s.project.folder);
    this.writing = this.writing
      .then(() =>
        saveSession(
          {
            file,
            project: { id: s.project.id, name: s.project.name, createdAt: s.project.createdAt, lastSaved: s.project.lastSaved, dirty: s.project.dirty, cloud: s.project.cloud },
            view: s.viewport,
            imageToken: token,
          },
          writeImage && s.image ? { token: token!, image: imageData(s.image) } : null,
        ),
      )
      .then(() => {
        this.savedImageToken = token;
        this.error = null;
      })
      .catch((e: unknown) => {
        const msg = e instanceof Error ? e.message : String(e);
        if (this.error !== msg) this.onError(msg);
        this.error = msg;
      });
    return this.writing;
  }
}
